// Small shared helpers for the Print Center views.

export const fill = (template, values = {}) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    String(template || "")
  );

/** `plural(tt, "labelCount", 3)` picks `labelCountOne` / `labelCountOther`. */
export const plural = (tt, key, count, values = {}) =>
  fill(tt(`${key}${Number(count) === 1 ? "One" : "Other"}`), { count, ...values });

export const newIdempotencyKey = (prefix) => {
  const random =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${random}`;
};

export const formatDateTime = (value, language = "sq") => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "sq-AL", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return String(value);
  }
};

export const formatDay = (isoDay, language = "sq") => {
  try {
    const [year, month, day] = String(isoDay).split("-").map(Number);
    return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "sq-AL", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date(Date.UTC(year, month - 1, day, 12)));
  } catch (_error) {
    return String(isoDay);
  }
};

/** "5 min ago" style age of a timestamp, for unconfirmed prints. */
export const formatRelative = (tt, value, now = Date.now()) => {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "—";
  const minutes = Math.max(0, Math.floor((now - time) / 60000));
  if (minutes < 1) return tt("relativeJustNow");
  if (minutes < 60) return fill(tt("relativeMinutes"), { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return fill(tt("relativeHours"), { count: hours });
  return fill(tt("relativeDays"), { count: Math.floor(hours / 24) });
};

export const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `${parsed.toFixed(2)}€` : "";
};

/** "Black / 38" style variant description, without repeating the product name. */
export const describeVariant = (item) => {
  const parts = [item?.color, item?.size].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return item?.variantTitle || "";
};

/** Error code and details from an RTK Query error with the API's error envelope. */
export const getApiErrorDetails = (error) => {
  const data = error?.data || {};
  return {
    status: error?.status ?? null,
    code: data?.details?.code || null,
    details: data?.details || null,
    message: data?.message || null,
  };
};

/**
 * Company, user and role from the signed-in token. Used only as a fallback
 * for scoping the session-stored selection and for showing admin-only
 * controls; the API enforces every permission itself.
 */
export const readTokenScope = (storage = typeof window !== "undefined" ? window.localStorage : null) => {
  try {
    const token = storage?.getItem("token");
    const payload = JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return {
      companyId: payload.companyId ?? payload.company_id ?? null,
      userId: payload.id ?? payload.sub ?? null,
      role: String(payload.role || "").toLowerCase() || null,
    };
  } catch (_error) {
    return { companyId: null, userId: null, role: null };
  }
};

// Print Center capabilities the CMS gates on, mirroring the backend's
// printing-permissions.constants.ts (the API enforces every capability,
// including running jobs and dismissing demand, which are manager+ today).
/** Open the Print Center (route + sidebar). */
export const PRINT_CENTER_VIEW_ROLES = ["manager", "admin", "owner"];
/** Change company-wide sheet presets. */
export const PRESET_ADMIN_ROLES = ["admin", "owner"];
